import React, { useState, useRef, useEffect } from 'react'
import { HandThumbsUpFill, HandThumbsDownFill, Circle, ChevronDown, ChevronUp } from 'react-bootstrap-icons'
import HandThumbsSidewaysFill from './HandThumbsSidewaysFill'
import axiosService from '../../utils/AxiosService'

const PENDING_OPTION = { value: 'pending', Icon: Circle, color: 'text-rb-lighter-grey dark:text-rb-dark-mode-text-grey', label: 'Pending' }

const OPTIONS = [
  { value: 'selected', Icon: HandThumbsUpFill, color: 'text-green-600 dark:text-green-400', label: 'Yes, fits the brand' },
  { value: 'rejected', Icon: HandThumbsDownFill, color: 'text-red-600 dark:text-red-400', label: 'Do not contact' },
  { value: 'backuped', Icon: HandThumbsSidewaysFill, color: 'text-yellow-600 dark:text-yellow-400', label: 'Backup' }
]

const STATUS_BY_INDEX = ['pending', 'selected', 'rejected', 'backuped']

function normalizeStatus(s) {
  if (s == null || s === '') return 'pending'
  return typeof s === 'number' ? STATUS_BY_INDEX[s] : s
}

export default function SelectionStatusPicker({
  campaignId,
  campaignAuthenticationId,
  patchUrl,
  selectionStatus,
  onSuccess,
  size = 20,
  className = ''
}) {
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [lastSelectedStatus, setLastSelectedStatus] = useState(null)
  const panelRef = useRef(null)

  const propStatusKey = normalizeStatus(selectionStatus)
  const displayStatusKey = lastSelectedStatus !== null ? normalizeStatus(lastSelectedStatus) : propStatusKey
  const statusKey = displayStatusKey

  useEffect(() => {
    setLastSelectedStatus(null)
  }, [propStatusKey])

  const currentOption = statusKey === 'pending' || !statusKey
    ? PENDING_OPTION
    : (OPTIONS.find(o => o.value === statusKey) || PENDING_OPTION)

  const getPatchUrl = () => {
    if (patchUrl) return patchUrl
    return `/api/project_manager/campaigns/${campaignId}/campaign_authentications/${campaignAuthenticationId}`
  }

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [open])

  const handleSelect = async (value) => {
    if (saving || value === statusKey) {
      setOpen(false)
      return
    }
    setSaving(true)
    try {
      const response = await axiosService.patch(
        getPatchUrl(),
        { campaign_authentication: { selection_status: value } }
      )
      const savedStatus = response?.selection_status ?? value
      setLastSelectedStatus(savedStatus)
      onSuccess?.(savedStatus)
      setOpen(false)
    } catch (err) {
      console.error('Failed to update selection status', err)
    } finally {
      setSaving(false)
    }
  }

  const CurrentIcon = currentOption.Icon

  return (
    <div className={`relative inline-block ${className}`} ref={panelRef}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o) }}
        className="flex items-center gap-2 border-1 border-widget-border dark:border-rb-dark-mode-lighter rounded-lg py-1 px-2 hover:bg-rb-grey dark:hover:bg-rb-dark-mode-lighter disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-white dark:focus:ring-offset-rb-dark-mode"
        title="Click to change selection status"
        aria-label="Change selection status"
        aria-haspopup="true"
        aria-expanded={open}
        disabled={saving}
      >
        <span className={currentOption.color}><CurrentIcon size={size} /></span>
        <span className="text-sm text-rb-black dark:text-rb-dark-mode-text-light">{currentOption.label}</span>
        {open ? <ChevronUp className="text-gray-500 dark:text-gray-400" size={14} /> : <ChevronDown className="text-gray-500 dark:text-gray-400" size={14} />}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-50 min-w-[180px] py-1 bg-white dark:bg-rb-dark-mode border border-gray-200 dark:border-rb-dark-mode-lighter rounded-lg shadow-lg"
          role="menu"
        >
          {[PENDING_OPTION, ...OPTIONS].map(({ value, Icon, color, label }) => (
            <button
              key={value}
              type="button"
              role="menuitem"
              onClick={(e) => { e.stopPropagation(); handleSelect(value) }}
              disabled={saving}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-rb-dark-mode-lighter disabled:opacity-60 disabled:cursor-not-allowed ${statusKey === value ? 'bg-gray-50 dark:bg-rb-dark-mode-lighter' : ''} ${color}`}
            >
              <Icon size={18} />
              <span className="text-rb-black dark:text-rb-dark-mode-text-light">{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
